import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { AlertTriangle, AlertOctagon, Info, Clock, CheckCircle2, RefreshCw, DollarSign, FileText, Briefcase } from 'lucide-react';
import logger from '@/lib/logger';

const SEVERITY_META = {
  critical: {
    label: 'Crítico',
    icon: AlertOctagon,
    badgeClass: 'bg-red-100 text-red-700 border-red-200',
    cardClass: 'border-red-200 bg-red-50/40',
    iconClass: 'text-red-600',
  },
  warning: {
    label: 'Atenção',
    icon: AlertTriangle,
    badgeClass: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    cardClass: 'border-yellow-200 bg-yellow-50/40',
    iconClass: 'text-yellow-600',
  },
  info: {
    label: 'Informativo',
    icon: Info,
    badgeClass: 'bg-blue-100 text-blue-700 border-blue-200',
    cardClass: 'border-blue-200 bg-blue-50/40',
    iconClass: 'text-blue-600',
  },
};

const CATEGORY_META = {
  financial: { label: 'Financeiro', icon: DollarSign, color: 'text-emerald-700' },
  license: { label: 'Licenças', icon: FileText, color: 'text-purple-700' },
  project: { label: 'Projetos', icon: Briefcase, color: 'text-blue-700' },
};

function AlertsPanel() {
  const [data, setData] = useState({ alerts: [], counts: {}, by_category: {} });
  const [loading, setLoading] = useState(true);
  const [severityFilter, setSeverityFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [busyId, setBusyId] = useState(null);

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/system-alerts');
      setData(res.data);
    } catch (e) {
      logger.error('Error fetching alerts:', e);
      toast.error('Erro ao carregar alertas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  const handleSnooze = async (alertId, days = 7) => {
    setBusyId(alertId);
    try {
      await api.post(`/system-alerts/${alertId}/snooze`, { days });
      toast.success(`Alerta adiado por ${days} dia(s)`);
      fetchAlerts();
    } catch (e) {
      logger.error('Error snoozing alert:', e);
      toast.error('Erro ao adiar alerta');
    } finally {
      setBusyId(null);
    }
  };

  const handleResolve = async (alertId) => {
    setBusyId(alertId);
    try {
      await api.post(`/system-alerts/${alertId}/resolve`);
      toast.success('Alerta resolvido');
      fetchAlerts();
    } catch (e) {
      logger.error('Error resolving alert:', e);
      toast.error('Erro ao resolver alerta');
    } finally {
      setBusyId(null);
    }
  };

  const filtered = useMemo(() => {
    return (data.alerts || []).filter((a) => {
      if (severityFilter !== 'all' && a.severity !== severityFilter) return false;
      if (categoryFilter !== 'all' && a.category !== categoryFilter) return false;
      return true;
    });
  }, [data.alerts, severityFilter, categoryFilter]);

  const counts = data.counts || {};
  const byCategory = data.by_category || {};

  return (
    <div className="space-y-4" data-testid="alerts-panel">
      {/* KPI summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-3 border border-red-200 bg-red-50">
          <div className="flex items-center gap-2">
            <AlertOctagon className="w-5 h-5 text-red-600" />
            <div>
              <p className="text-xs text-red-700">Críticos</p>
              <p className="text-xl font-bold text-red-800" data-testid="alerts-count-critical">{counts.critical || 0}</p>
            </div>
          </div>
        </Card>
        <Card className="p-3 border border-yellow-200 bg-yellow-50">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-yellow-600" />
            <div>
              <p className="text-xs text-yellow-700">Atenção</p>
              <p className="text-xl font-bold text-yellow-800" data-testid="alerts-count-warning">{counts.warning || 0}</p>
            </div>
          </div>
        </Card>
        <Card className="p-3 border border-blue-200 bg-blue-50">
          <div className="flex items-center gap-2">
            <Info className="w-5 h-5 text-blue-600" />
            <div>
              <p className="text-xs text-blue-700">Informativo</p>
              <p className="text-xl font-bold text-blue-800" data-testid="alerts-count-info">{counts.info || 0}</p>
            </div>
          </div>
        </Card>
        <Card className="p-3 border border-gray-200">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-gray-600" />
            <div>
              <p className="text-xs text-gray-700">Total ativo</p>
              <p className="text-xl font-bold text-gray-800" data-testid="alerts-count-total">{counts.total || 0}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Filters + refresh */}
      <Card className="p-3 border border-gray-200">
        <div className="flex flex-wrap items-center gap-3">
          <Select value={severityFilter} onValueChange={setSeverityFilter}>
            <SelectTrigger className="w-40" data-testid="alerts-filter-severity">
              <SelectValue placeholder="Severidade" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas severidades</SelectItem>
              <SelectItem value="critical">Crítico</SelectItem>
              <SelectItem value="warning">Atenção</SelectItem>
              <SelectItem value="info">Informativo</SelectItem>
            </SelectContent>
          </Select>

          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-44" data-testid="alerts-filter-category">
              <SelectValue placeholder="Categoria" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas categorias</SelectItem>
              <SelectItem value="financial">Financeiro ({byCategory.financial || 0})</SelectItem>
              <SelectItem value="license">Licenças ({byCategory.license || 0})</SelectItem>
              <SelectItem value="project">Projetos ({byCategory.project || 0})</SelectItem>
            </SelectContent>
          </Select>

          <div className="flex-1" />

          <Button
            variant="outline"
            size="sm"
            onClick={fetchAlerts}
            disabled={loading}
            data-testid="alerts-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Recalcular
          </Button>
        </div>
      </Card>

      {/* Alert list */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-800"></div>
        </div>
      ) : filtered.length === 0 ? (
        <Card className="p-12 border border-green-200 bg-green-50/40 text-center">
          <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto mb-3" />
          <p className="text-lg font-medium text-green-800">Tudo em ordem!</p>
          <p className="text-sm text-green-700 mt-1">
            {data.alerts?.length > 0
              ? 'Nenhum alerta corresponde aos filtros selecionados.'
              : 'Nenhum alerta ativo no momento.'}
          </p>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="alerts-list">
          {filtered.map((alert) => {
            const sev = SEVERITY_META[alert.severity] || SEVERITY_META.info;
            const cat = CATEGORY_META[alert.category] || { label: alert.category, icon: Info, color: 'text-gray-600' };
            const SevIcon = sev.icon;
            const CatIcon = cat.icon;
            return (
              <Card
                key={alert.alert_id}
                className={`p-4 border ${sev.cardClass}`}
                data-testid={`alert-${alert.alert_id}`}
              >
                <div className="flex items-start gap-3">
                  <SevIcon className={`w-5 h-5 mt-0.5 shrink-0 ${sev.iconClass}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <Badge variant="outline" className={sev.badgeClass}>
                        {sev.label}
                      </Badge>
                      <span className={`text-xs inline-flex items-center gap-1 ${cat.color}`}>
                        <CatIcon className="w-3 h-3" />
                        {cat.label}
                      </span>
                    </div>
                    <p className="font-medium text-gray-900 break-words">{alert.title}</p>
                    <p className="text-sm text-gray-600 mt-0.5 break-words">{alert.message}</p>
                    {alert.link && (
                      <a
                        href={alert.link}
                        className="text-xs text-emerald-700 hover:underline mt-1 inline-block"
                        data-testid={`alert-link-${alert.alert_id}`}
                      >
                        → Abrir
                      </a>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleSnooze(alert.alert_id, 7)}
                      disabled={busyId === alert.alert_id}
                      title="Adiar 7 dias"
                      data-testid={`alert-snooze-${alert.alert_id}`}
                    >
                      <Clock className="w-4 h-4 text-yellow-600" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleResolve(alert.alert_id)}
                      disabled={busyId === alert.alert_id}
                      title="Marcar como resolvido"
                      data-testid={`alert-resolve-${alert.alert_id}`}
                    >
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default AlertsPanel;
